import { useMemo } from 'react';

import { EMPTY_FILTERS } from './serviceListFilters';
import { ServiceStatusResponse } from '../../@types/service.type';
import { StructureResponse } from '../../@types/structure.type';
import { FilterState } from '../../components/molecules/FilterBar';

export const useServiceLookups = (
  statusesData: ServiceStatusResponse[] | undefined,
  clientsData: StructureResponse[] | undefined
): {
  statusCodeToName: Record<string, string>;
  statusNameToCodes: Record<string, string[]>;
  clientIdToName: Record<string, string>;
  clientNameToId: Record<string, string>;
  openStatusFilterState: FilterState;
} => {
  const statusCodeToName = useMemo<Record<string, string>>(
    () => Object.fromEntries((statusesData ?? []).map((s) => [s.code, s.name])),
    [statusesData]
  );

  const statusNameToCodes = useMemo<Record<string, string[]>>(() => {
    const map: Record<string, string[]> = {};
    for (const s of statusesData ?? []) {
      if (!map[s.name]) map[s.name] = [];
      map[s.name].push(s.code);
    }
    return map;
  }, [statusesData]);

  const clientIdToName = useMemo<Record<string, string>>(
    () => Object.fromEntries((clientsData ?? []).map((c) => [String(c.id), c.name])),
    [clientsData]
  );

  const clientNameToId = useMemo<Record<string, string>>(
    () => Object.fromEntries((clientsData ?? []).map((c) => [c.name, String(c.id)])),
    [clientsData]
  );

  const openStatusFilterState = useMemo<FilterState>(() => {
    const data = statusesData;
    if (!data?.length) return EMPTY_FILTERS;
    const codeToName: Record<string, string> = {};
    const openCodes: string[] = [];
    for (const s of data) {
      if (s.isActive && !s.isClosed) {
        codeToName[s.code] = s.name;
        openCodes.push(s.code);
      }
    }
    if (openCodes.length === 0) return EMPTY_FILTERS;
    return { ...EMPTY_FILTERS, statuses: openCodes, statusCodeToName: codeToName };
  }, [statusesData]);

  return {
    statusCodeToName,
    statusNameToCodes,
    clientIdToName,
    clientNameToId,
    openStatusFilterState,
  };
};
